import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { BRAND } from "../../data/content";

export default function ChangePassword() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) return toast.error("Password must be at least 8 characters");
    if (form.new_password !== form.confirm_password) return toast.error("Passwords don't match");
    if (form.new_password === form.current_password) return toast.error("New password must differ from current");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password updated — please sign in again");
      // Force a fresh login so the JWT carries the new must_change_password=false claim
      logout();
      navigate("/admin/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const isForced = user?.must_change_password;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6" data-testid="page-change-password">
      <form onSubmit={submit} className="bg-white border border-slate-200 max-w-md w-full p-8 shadow-sm">
        <div className="flex flex-col items-center mb-6">
          <span className="bg-white px-3 py-2 rounded-sm">
            <img src={BRAND.logo} alt="OEC" className="h-10 w-auto" />
          </span>
        </div>
        <div className="flex items-start gap-3 mb-6">
          <ShieldCheck className="w-5 h-5 text-[#1FA59A] mt-0.5 flex-shrink-0" />
          <div>
            <h1 className="font-display text-xl font-bold text-slate-900">Set a new password</h1>
            <p className="text-sm text-slate-500 mt-1">{isForced ? "Your account uses a temporary password set by an administrator. Please choose your own before continuing." : "Update your password."}</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Current password</label>
            <input required type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#1FA59A]" data-testid="cp-current" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">New password</label>
            <input required type="password" minLength={8} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#1FA59A]" data-testid="cp-new" />
            <p className="text-xs text-slate-500 mt-1">Minimum 8 characters · mix of letters &amp; numbers recommended</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">Confirm new password</label>
            <input required type="password" minLength={8} value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} className="w-full border border-slate-300 px-4 py-3 focus:outline-none focus:border-[#1FA59A]" data-testid="cp-confirm" />
          </div>
        </div>
        <button type="submit" disabled={busy} className="gs-gradient text-white font-bold px-6 py-3 w-full mt-6 hover:opacity-90 disabled:opacity-50" data-testid="cp-submit">
          {busy ? "Updating…" : "Update password"}
        </button>
        {!isForced && (
          <button type="button" onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900 mt-4 mx-auto block">Cancel</button>
        )}
      </form>
    </div>
  );
}
